import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Disc3, Sparkles, LayoutDashboard, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardTarget } from '@/hooks/useDashboardTarget';

// Guided navigation band — the 4 approved wayfinding cards placed directly
// under the hero: Events / DJs / Join SoundWolves / Dashboard.
// When the visitor is already signed in, the "Join SoundWolves" card becomes
// an account shortcut so we never ask an authenticated user to sign up again.
export function GuidedNavCards() {
  const { isAuthenticated } = useAuth();
  const dashboardTo = useDashboardTarget();

  const cards = [
    { label: 'Events', desc: 'Browse upcoming nights & shows', icon: Calendar, to: '/events', tourId: 'tour-events' },
    { label: 'DJs', desc: 'Discover & follow the lineup', icon: Disc3, to: '/djs', tourId: 'tour-djs' },
    {
      label: isAuthenticated ? 'My Account' : 'Join SoundWolves',
      desc: isAuthenticated ? 'Manage your SoundWolves profile' : 'Create your account in seconds',
      icon: Sparkles,
      to: isAuthenticated ? '/profile' : '/login',
      tourId: 'tour-join',
    },
    { label: 'Dashboard', desc: 'Your events, tickets & tools', icon: LayoutDashboard, to: dashboardTo, tourId: 'tour-dashboard' },
  ];

  return (
    <section className="py-10 animate-fade-in">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.label} to={c.to} className="group">
                <div
                  data-tour={c.tourId}
                  className="glass-card hover-lift rounded-xl p-5 h-full border border-border/50 flex flex-col gap-3 scroll-mt-24"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-lg group-hover:text-primary transition-colors">
                      {c.label}
                    </h3>
                    <p className="text-sm text-muted-foreground hidden sm:block">{c.desc}</p>
                  </div>
                  <div className="flex items-center text-primary text-sm font-medium">
                    Explore
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default GuidedNavCards;
